import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { roundTo } from '../types/geometry.js';
import { analyseRedEye, DEFAULT_RED_EYE_PARAMS } from '../face/redEye.js';
import { missingRequirementReason, notChecked, plainResult } from './support.js';

/**
 * Red-eye check.
 *
 * Only the iris/pupil ROI is read, and only connected clusters of clearly
 * red-dominant pixels count — never a single pixel, never the skin, the lids,
 * the makeup or a red frame. The finding is deliberately about the
 * *photo* (a new shot without a flash in the face), never about retouching the
 * eyes of the person.
 */
export const redEyeCheck: ValidationCheck = {
  id: 'red_eye',
  title: 'Красные глаза',
  description: 'Ищет красный зрачковый рефлекс только в области зрачков (ROI по landmarks).',
  kind: 'content',
  requires: ['pixels', 'geometry'],

  run: (ctx): CheckResult => {
    const missing = missingRequirementReason(ctx, ['pixels', 'geometry']);
    if (missing) return notChecked(ctx, redEyeCheck, missing, ctx.rule.severity);
    const geometry = ctx.geometry;
    const pixels = ctx.pixels;
    if (!geometry || !pixels) {
      return notChecked(ctx, redEyeCheck, 'Нет данных для анализа области зрачков.', ctx.rule.severity);
    }

    const minRedDominance = numberParam(
      ctx.rule.params,
      'minRedDominance',
      DEFAULT_RED_EYE_PARAMS.minRedDominance,
    );
    const minRedSaturation = numberParam(
      ctx.rule.params,
      'minRedSaturation',
      DEFAULT_RED_EYE_PARAMS.minRedSaturation,
    );
    const minRedPixelRatio = numberParam(
      ctx.rule.params,
      'minRedPixelRatio',
      DEFAULT_RED_EYE_PARAMS.minRedPixelRatio,
    );
    const maxGlassesRedRatio = numberParam(
      ctx.rule.params,
      'maxGlassesRedRatio',
      DEFAULT_RED_EYE_PARAMS.maxGlassesRedRatio,
    );
    const uncertainBelow = numberParam(ctx.rule.params, 'uncertainBelowConfidence', 0.6);

    const face = ctx.faces.faces[0];
    const analysis = analyseRedEye({
      pixels,
      geometry,
      ...(face?.landmarks ? { landmarks: face.landmarks } : {}),
      params: { minRedDominance, minRedSaturation, minRedPixelRatio, maxGlassesRedRatio },
    });

    const confident = analysis.confidence >= uncertainBelow;
    const status: CheckResult['status'] =
      analysis.status === 'clear'
        ? 'pass'
        : analysis.status === 'suspected' && confident
          ? 'warning'
          : 'uncertain';

    const message = (() => {
      if (status === 'pass') return '✅ Красных глаз не обнаружено';
      if (analysis.status === 'suspected') return '⚠ Обнаружен возможный red-eye эффект';
      if (analysis.status === 'roi_too_small') {
        return '⚠ Область зрачков слишком мала для надёжной проверки.';
      }
      return '⚠ Не удалось надёжно подтвердить red-eye: красная область слишком велика для зрачка.';
    })();

    const offending = analysis.rois.filter((roi) => roi.clusteredPixels > 0);

    return plainResult({
      id: redEyeCheck.id,
      title: redEyeCheck.title,
      status,
      // A red-eye finding is always a soft one: it never becomes a hard failure.
      severity: status === 'warning' ? 'warning' : ctx.rule.severity,
      value: analysis.redPixelRatio,
      max: minRedPixelRatio,
      unit: 'ratio',
      ...(ctx.rule.official ? { official: ctx.rule.official } : {}),
      message,
      ...(ctx.rule.official?.statement ? { requirement: ctx.rule.official.statement } : {}),
      fixability: 'retake',
      geometry: analysis.rois.map((roi) => ({
        type: 'rect' as const,
        coordinates: [
          roundTo(roi.rect.x, 4),
          roundTo(roi.rect.y, 4),
          roundTo(roi.rect.width, 4),
          roundTo(roi.rect.height, 4),
        ],
      })),
      problemRegions:
        status === 'pass' || offending.length === 0
          ? []
          : offending.map((roi) => ({
              id: `red_eye_${roi.id}`,
              rect: roi.rect,
              message: 'Возможный red-eye в области зрачка.',
              severity: 'warning' as const,
            })),
      confidence: analysis.confidence,
      details: {
        redPixelRatio: analysis.redPixelRatio,
        rawRedPixelRatio: analysis.rawRedPixelRatio,
        clusterCount: analysis.clusterCount,
        largestCluster: analysis.largestCluster,
        roiPixels: analysis.roiPixels,
        minRedDominance,
        minRedSaturation,
        minRedPixelRatio,
        maxGlassesRedRatio,
        uncertainBelowConfidence: uncertainBelow,
        mode: 'heuristic',
        notes: analysis.notes,
        displayValue: status === 'pass' ? 'не обнаружено' : 'возможен red-eye',
      },
    });
  },
};
